import type { DateTime } from 'luxon'

import type { FormattedRow } from '@/features/bessDashboard/types'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevel, BidPricing } from '@/features/bidding/types/bidLadder'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { PricingStrategyConfig } from '@/features/bidding/types/forecastPricing'
import {
  deriveForecastPricesForDirection,
  getFallbackDailyPrice,
} from '@/features/bidding/utils/cascading/bidLadder/deriveForecastPrices'
import {
  buildAggregateHourlyMap,
  resolveVolumeCapacityAverage,
} from '@/features/bidding/utils/cascading/bidLadder/resolveVolumeCapacity'

/**
 * Converts BidLevelInput[] from the bid ladder input component to BidLevel[]
 * format expected by the bid ladder chart.
 *
 * Each BidLevelInput can produce 0-2 BidLevel entries:
 * - One for 'up' direction (if has UP groups AND upPrice is set OR forecast mode)
 * - One for 'down' direction (if has DOWN groups AND downPrice is set OR forecast mode)
 *
 * When pricingConfig indicates forecast mode for a level, daily average prices
 * are derived from forecast data instead of using the fixed price inputs.
 *
 * Bid levels are sorted by price ascending (merit order) and cumulative volumes are calculated.
 */
export function convertBidLevelInputsToBidLevels(
  levels: BidLevelInput[],
  pricingConfig?: PricingStrategyConfig,
  forecastsData?: FormattedRow[],
  selectedDeliveryDay?: DateTime,
  aggregateSuggestedBids?: AggregateSuggestedBidResponse | null,
  aggregateAvailability?: AggregateAvailabilityResponse | null,
  granularityMW = 0.1,
): BidLevel[] {
  const suggestedUpMap = buildAggregateHourlyMap(aggregateSuggestedBids, 'up')
  const suggestedDownMap = buildAggregateHourlyMap(aggregateSuggestedBids, 'down')
  const availableUpMap = buildAggregateHourlyMap(aggregateAvailability, 'up')
  const availableDownMap = buildAggregateHourlyMap(aggregateAvailability, 'down')

  const upLevels: BidLevel[] = []
  const downLevels: BidLevel[] = []

  for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
    const level = levels[levelIndex]
    const levelPricingConfig = pricingConfig?.levels[levelIndex]
    const isForecastMode = levelPricingConfig?.mode === 'forecast'

    const upGroups = level.assignedGroups.filter((g) => g.direction === 'up')
    const downGroups = level.assignedGroups.filter((g) => g.direction === 'down')

    // Derive forecast prices if in forecast mode
    let forecastUpPrice: number | null = null
    let forecastDownPrice: number | null = null

    if (isForecastMode && levelPricingConfig && forecastsData && selectedDeliveryDay) {
      const upHourlyPrices = deriveForecastPricesForDirection(
        forecastsData,
        levelPricingConfig.market,
        'up',
        levelPricingConfig.quantile,
        selectedDeliveryDay,
      )
      const downHourlyPrices = deriveForecastPricesForDirection(
        forecastsData,
        levelPricingConfig.market,
        'down',
        levelPricingConfig.quantile,
        selectedDeliveryDay,
      )
      forecastUpPrice = getFallbackDailyPrice(upHourlyPrices)
      forecastDownPrice = getFallbackDailyPrice(downHourlyPrices)
    }

    // Determine effective prices (forecast takes precedence when in forecast mode)
    const effectiveUpPrice = isForecastMode ? forecastUpPrice : level.upPrice
    const effectiveDownPrice = isForecastMode ? forecastDownPrice : level.downPrice

    // Create UP bid level if has groups and price
    if (upGroups.length > 0 && effectiveUpPrice !== null) {
      const volumeMW = upGroups.reduce((sum, g) => sum + g.capacityMW, 0)
      const pricing: BidPricing = isForecastMode
        ? {
            type: 'forecast',
            avgPrice: effectiveUpPrice,
          }
        : {
            type: 'fixed',
            fixedPrice: effectiveUpPrice,
            avgPrice: effectiveUpPrice,
          }

      upLevels.push({
        volumeMW,
        cumulativeVolumeMW: 0,
        clearingProbability: 0,
        market: 'up',
        pricing,
      })
    } else if (upGroups.length === 0 && level.upVolumeCapacity?.value != null && effectiveUpPrice !== null) {
      // FCR volume capacity fallback (no activation groups)
      const volumeMW = resolveVolumeCapacityAverage(
        level.upVolumeCapacity,
        suggestedUpMap,
        availableUpMap,
        granularityMW,
      )
      if (volumeMW > 0) {
        const pricing: BidPricing = isForecastMode
          ? { type: 'forecast', avgPrice: effectiveUpPrice }
          : { type: 'fixed', fixedPrice: effectiveUpPrice, avgPrice: effectiveUpPrice }
        upLevels.push({ volumeMW, cumulativeVolumeMW: 0, clearingProbability: 0, market: 'up', pricing })
      }
    }

    // Create DOWN bid level if has groups and price
    if (downGroups.length > 0 && effectiveDownPrice !== null) {
      const volumeMW = downGroups.reduce((sum, g) => sum + g.capacityMW, 0)
      const pricing: BidPricing = isForecastMode
        ? {
            type: 'forecast',
            avgPrice: effectiveDownPrice,
          }
        : {
            type: 'fixed',
            fixedPrice: effectiveDownPrice,
            avgPrice: effectiveDownPrice,
          }

      downLevels.push({
        volumeMW,
        cumulativeVolumeMW: 0,
        clearingProbability: 0,
        market: 'down',
        pricing,
      })
    } else if (downGroups.length === 0 && level.downVolumeCapacity?.value != null && effectiveDownPrice !== null) {
      // FCR volume capacity fallback (no activation groups)
      const volumeMW = resolveVolumeCapacityAverage(
        level.downVolumeCapacity,
        suggestedDownMap,
        availableDownMap,
        granularityMW,
      )
      if (volumeMW > 0) {
        const pricing: BidPricing = isForecastMode
          ? { type: 'forecast', avgPrice: effectiveDownPrice }
          : { type: 'fixed', fixedPrice: effectiveDownPrice, avgPrice: effectiveDownPrice }
        downLevels.push({ volumeMW, cumulativeVolumeMW: 0, clearingProbability: 0, market: 'down', pricing })
      }
    }
  }

  // Sort by price ascending (merit order - lowest price first)
  upLevels.sort((a, b) => {
    const priceA = a.pricing.type === 'fixed' ? a.pricing.fixedPrice : a.pricing.avgPrice
    const priceB = b.pricing.type === 'fixed' ? b.pricing.fixedPrice : b.pricing.avgPrice
    return priceA - priceB
  })

  downLevels.sort((a, b) => {
    const priceA = a.pricing.type === 'fixed' ? a.pricing.fixedPrice : a.pricing.avgPrice
    const priceB = b.pricing.type === 'fixed' ? b.pricing.fixedPrice : b.pricing.avgPrice
    return priceA - priceB
  })

  // Calculate cumulative volumes
  let cumUp = 0
  for (const level of upLevels) {
    cumUp += level.volumeMW
    level.cumulativeVolumeMW = cumUp
  }

  let cumDown = 0
  for (const level of downLevels) {
    cumDown += level.volumeMW
    level.cumulativeVolumeMW = cumDown
  }

  return [...upLevels, ...downLevels]
}
